import type { CartLineItem, Product, ProductVariant } from '~/types'
import { resolveCurrentClientShopSlug } from '~/utils/resolveShopSlug'
import {
  readShopSessionStorage,
  removeShopSessionStorage,
  writeShopSessionStorage,
} from '~/utils/shopStorage'

export interface GtmEcommerceItem {
  item_id: string
  item_name: string
  price: number
  quantity: number
  item_brand?: string
  item_category?: string
  item_variant?: string
}

export interface GtmPurchasePayload {
  transaction_id: string
  value: number
  shipping?: number
  tax?: number
  coupon?: string
  items: GtmEcommerceItem[]
}

const PURCHASE_STORAGE_SUFFIX = 'pending_purchase'
const PURCHASE_TRACKED_SUFFIX = 'purchase_tracked'
const LEGACY_PURCHASE_STORAGE_KEY = 'cdt-pending-purchase'
const LEGACY_PURCHASE_TRACKED_KEY = 'cdt-purchase-tracked'

function currentSlug(): string {
  return resolveCurrentClientShopSlug()
}

function toPrice(value?: number | string | null): number {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

export function productToGtmItem(
  product: Product,
  quantity = 1,
  variant?: ProductVariant,
): GtmEcommerceItem {
  const unitPrice = toPrice(variant?.salePrice ?? variant?.price ?? product.salePrice ?? product.price)
  const variantLabel = [variant?.color, variant?.storage].filter(Boolean).join(' / ')
    || [product.color, product.storage].filter(Boolean).join(' / ')

  return {
    item_id: product.sku || String(product.id),
    item_name: product.name,
    price: unitPrice,
    quantity: Math.max(1, quantity),
    item_brand: product.brand?.name,
    item_category: product.category?.name,
    item_variant: variantLabel || undefined,
  }
}

export function cartLineToGtmItem(item: CartLineItem): GtmEcommerceItem {
  if (!item.product) {
    return {
      item_id: String(item.productId),
      item_name: 'Produs',
      price: toPrice(item.unitPrice),
      quantity: item.quantity,
    }
  }

  return productToGtmItem(item.product, item.quantity, item.variant)
}

export function pushGtmEcommerceEvent(
  event: string,
  params: Record<string, unknown>,
): void {
  if (!import.meta.client) return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event,
    ecommerce: params,
  })
}

export function savePendingPurchase(payload: GtmPurchasePayload): void {
  if (!import.meta.client) return
  const slug = currentSlug()
  if (!slug) return
  writeShopSessionStorage(
    slug,
    PURCHASE_STORAGE_SUFFIX,
    JSON.stringify(payload),
    LEGACY_PURCHASE_STORAGE_KEY,
  )
  removeShopSessionStorage(slug, PURCHASE_TRACKED_SUFFIX, LEGACY_PURCHASE_TRACKED_KEY)
}

export function consumePendingPurchase(): GtmPurchasePayload | null {
  if (!import.meta.client) return null
  const slug = currentSlug()
  if (!slug) return null

  const raw = readShopSessionStorage(
    slug,
    PURCHASE_STORAGE_SUFFIX,
    LEGACY_PURCHASE_STORAGE_KEY,
  )
  if (!raw) return null

  try {
    return JSON.parse(raw) as GtmPurchasePayload
  } catch {
    removeShopSessionStorage(slug, PURCHASE_STORAGE_SUFFIX, LEGACY_PURCHASE_STORAGE_KEY)
    return null
  }
}

export function markPurchaseTracked(transactionId: string): boolean {
  if (!import.meta.client) return false
  const slug = currentSlug()
  if (!slug) return false

  const tracked = readShopSessionStorage(
    slug,
    PURCHASE_TRACKED_SUFFIX,
    LEGACY_PURCHASE_TRACKED_KEY,
  )
  if (tracked === transactionId) return false
  writeShopSessionStorage(slug, PURCHASE_TRACKED_SUFFIX, transactionId, LEGACY_PURCHASE_TRACKED_KEY)
  removeShopSessionStorage(slug, PURCHASE_STORAGE_SUFFIX, LEGACY_PURCHASE_STORAGE_KEY)
  return true
}
